import { useState, useRef, type FormEvent } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { Loader2, Mail, KeyRound, ShieldCheck, ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@/api/client';
import { AuthShell, HipaaBadge, AuthErrorBanner } from '@/components/auth/AuthShell';
import { cn } from '@/lib/cn';

type ChallengeMode = 'totp' | 'email' | 'recovery';

export function MfaChallengePage() {
  const { pendingChallenge, loginMfa, requestOtpFallback, cancelChallenge } = useAuth();
  const navigate    = useNavigate();
  const inputRefs   = useRef<(HTMLInputElement | null)[]>([null,null,null,null,null,null]);
  const [mode, setMode] = useState<ChallengeMode>(() =>
    pendingChallenge?.step === 'mfa_otp' ? 'email' : 'totp',
  );
  const [digits, setDigits]             = useState(['','','','','','']);
  const [recoveryCode, setRecovery]     = useState('');
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [requestingOtp, setRequestingOtp] = useState(false);
  const [shake, setShake]               = useState(false);

  if (!pendingChallenge) return <Navigate to="/login" replace />;
  if (pendingChallenge.step === 'mfa_enrol_required') return <Navigate to="/auth/mfa/enrol" replace />;

  const submitCode = mode === 'recovery' ? recoveryCode : digits.join('');
  const isComplete = mode === 'recovery' ? recoveryCode.trim().length >= 6 : digits.every(d => d !== '');

  function switchMode(next: ChallengeMode) {
    setMode(next);
    setDigits(['','','','','','']);
    setRecovery('');
    setError(null);
    setShake(false);
  }

  function handleDigitChange(i: number, val: string) {
    const ch = val.replace(/\D/g,'').slice(-1);
    const next = [...digits];
    next[i] = ch;
    setDigits(next);
    if (ch && i < 5) inputRefs.current[i + 1]?.focus();
  }

  function handleDigitKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (digits[i]) {
        const next = [...digits]; next[i] = ''; setDigits(next);
      } else if (i > 0) {
        const next = [...digits]; next[i - 1] = ''; setDigits(next);
        inputRefs.current[i - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && i > 0)  { inputRefs.current[i - 1]?.focus(); }
      else if (e.key === 'ArrowRight' && i < 5) { inputRefs.current[i + 1]?.focus(); }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g,'').slice(0, 6);
    const next = ['','','','','',''];
    pasted.split('').forEach((ch, idx) => { next[idx] = ch; });
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  }

  async function handleRequestOtp() {
    setError(null);
    setRequestingOtp(true);
    try {
      await requestOtpFallback();
      switchMode('email');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setError('Too many requests. Please wait before requesting another code.');
      } else {
        setError('Could not send the email code. Please try again.');
      }
    } finally {
      setRequestingOtp(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const next = await loginMfa(mode, submitCode.trim());
      if (next === 'terms_acceptance_required') {
        navigate('/auth/accept-terms', { replace: true });
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      let msg: string;
      if (err instanceof ApiError) {
        if (err.code === 'RATE_LIMITED') {
          msg = 'Too many verification attempts. Please wait before trying again.';
        } else if (err.code === 'VALIDATION_ERROR') {
          msg = mode === 'recovery'
            ? 'Please enter a valid recovery code.'
            : 'Please enter a valid 6-digit code.';
        } else if (err.code === 'FORBIDDEN') {
          msg = err.message;
        } else {
          if (mode === 'recovery') {
            msg = 'Recovery code not recognised. Please check and try again.';
          } else if (mode === 'email') {
            msg = 'Incorrect or expired code. Please check your email and try again.';
          } else {
            msg = 'Incorrect code. Please check your authenticator app and try again.';
          }
        }
      } else {
        msg = 'Verification failed. Please try again.';
      }
      setError(msg);
      setShake(true);
      setTimeout(() => setShake(false), 400);
      if (mode !== 'recovery') setDigits(['','','','','','']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setSubmitting(false);
    }
  }

  const modeConfig = {
    totp:     { icon: <KeyRound size={18} className="text-navy"/>,     label: 'Authenticator code', hint: 'Open your authenticator app and enter the 6-digit code.' },
    email:    { icon: <Mail size={18} className="text-navy"/>,         label: 'Email verification', hint: `Code sent to ${pendingChallenge.otpDestinationMasked ?? 'your email'}. Valid for 5 minutes.` },
    recovery: { icon: <ShieldCheck size={18} className="text-navy"/>,  label: 'Recovery code',      hint: 'Enter one of the 12-character codes you saved when you set up MFA.' },
  }[mode];

  return (
    <AuthShell
      title={<>Two-factor<br/>authentication</>}
      lede="Verify your identity with a second factor to complete sign-in securely."
    >
      {/* Mode indicator */}
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 grid place-items-center rounded-sm bg-navy-50 border border-navy/15 shrink-0">
          {modeConfig.icon}
        </div>
        <div>
          <p className="eyebrow mb-0.5">Verification · {modeConfig.label}</p>
          <p className="text-[12px] text-text-3 leading-snug">{modeConfig.hint}</p>
        </div>
      </div>

      {/* Card */}
      <div className="relative bg-white border border-border rounded-sm p-8 pb-7 shadow-card">
        <HipaaBadge />

        <form onSubmit={handleSubmit} noValidate>
          {mode !== 'recovery' ? (
            <div className="flex gap-2.5 justify-center mb-6 pt-2">
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  className={cn(
                    'w-[54px] h-16 rounded-md border-[1.5px] bg-white text-[26px] font-extrabold font-mono text-text-1 text-center outline-none transition-all',
                    'focus:border-navy focus:border-2',
                    d ? 'border-navy border-2 bg-navy-50/40' : 'border-border',
                    shake && error && 'border-critical animate-[shake_0.35s_ease]',
                  )}
                  style={{ caretColor: 'transparent' }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  autoFocus={i === 0}
                  onChange={e => handleDigitChange(i, e.target.value)}
                  onKeyDown={e => handleDigitKeyDown(i, e)}
                  onPaste={i === 0 ? handlePaste : undefined}
                  onFocus={e => e.target.select()}
                />
              ))}
            </div>
          ) : (
            <div className="mb-6 pt-2">
              <input
                type="text"
                inputMode="text"
                maxLength={14}
                value={recoveryCode}
                onChange={e => setRecovery(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX"
                className="w-full h-[54px] rounded-md border-[1.5px] border-border bg-white font-mono font-bold text-[17px] tracking-[0.18em] text-center text-text-1 outline-none px-4 transition-colors focus:border-navy focus:border-2"
                autoFocus
              />
            </div>
          )}

          {error && <div className="mb-4"><AuthErrorBanner message={error} /></div>}

          <button
            type="submit"
            disabled={submitting || !isComplete}
            className="w-full h-12 bg-navy hover:bg-navy-600 active:bg-navy-700 text-white font-bold text-[13.5px] tracking-wide rounded-sm flex items-center justify-center gap-1.5 transition-colors shadow-card disabled:opacity-55 disabled:cursor-not-allowed mb-5"
          >
            {submitting
              ? <><Loader2 size={14} className="animate-spin"/> Verifying</>
              : <>Continue <ArrowRight size={14} className="opacity-75" /></>}
          </button>

          {/* Mode switching */}
          <div className="border-t border-border-soft pt-3 flex flex-col gap-0.5">
            {mode !== 'email' && (
              <button
                type="button"
                onClick={handleRequestOtp}
                disabled={requestingOtp}
                className="inline-flex items-center gap-1.5 w-full px-3 py-2 rounded-sm text-[12px] font-bold text-navy hover:bg-navy-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {requestingOtp
                  ? <><Loader2 size={13} className="animate-spin"/> Sending code</>
                  : <><Mail size={13}/> Use email code instead</>}
              </button>
            )}
            {mode !== 'recovery' && (
              <button
                type="button"
                onClick={() => switchMode('recovery')}
                className="inline-flex items-center gap-1.5 w-full px-3 py-2 rounded-sm text-[12px] font-bold text-navy hover:bg-navy-50 transition-colors"
              >
                <ShieldCheck size={13}/> Use a recovery code
              </button>
            )}
            {mode === 'recovery' && (
              <button
                type="button"
                onClick={() => switchMode('totp')}
                className="inline-flex items-center gap-1.5 w-full px-3 py-2 rounded-sm text-[12px] font-bold text-navy hover:bg-navy-50 transition-colors"
              >
                <KeyRound size={13}/> Back to authenticator code
              </button>
            )}
          </div>

          <div className="border-t border-border-soft pt-4 mt-1 text-center">
            <Link
              to="/login"
              onClick={cancelChallenge}
              className="inline-flex items-center gap-1.5 text-[12px] font-bold text-navy hover:underline"
            >
              <ArrowLeft size={13}/> Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </AuthShell>
  );
}
